import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://wowrolla.ca';

const SAME_AS = [
  'https://www.linkedin.com/company/wowrolla',
  'https://www.youtube.com/@wowrolla',
  'https://x.com/wowrolla',
];

const asText = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
};

const trimSlash = (value) => value.replace(/^\/+|\/+$/g, '');

const buildPath = (slug = '') => {
  const clean = trimSlash(asText(slug));
  return clean ? `/${clean}` : '/';
};

const toIso = (value) => {
  if (!value) {
    return new Date().toISOString();
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date().toISOString();
  }
  return date.toISOString();
};

const createBreadcrumbs = (slug = '') => {
  const clean = trimSlash(asText(slug));
  const parts = clean ? clean.split('/') : [];
  const crumbs = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Wowrolla',
      item: BASE_URL,
    },
  ];

  let running = '';
  parts.forEach((part, index) => {
    running += `/${part}`;
    crumbs.push({
      '@type': 'ListItem',
      position: index + 2,
      name: part.replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()),
      item: `${BASE_URL}${running}`,
    });
  });

  return {
    '@type': 'BreadcrumbList',
    itemListElement: crumbs,
  };
};

const normalizeFaq = (faqs = []) => {
  return (Array.isArray(faqs) ? faqs : [])
    .filter((faq) => faq && faq.q && faq.a)
    .map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    }));
};

const buildSoftwareApplication = (tool) => {
  if (!tool) {
    return null;
  }

  const firstTier = Array.isArray(tool.pricing_tiers) && tool.pricing_tiers.length > 0 ? tool.pricing_tiers[0] : null;
  const priceRaw = firstTier?.price || tool.avg_price || '0';
  const numericPrice = parseFloat(asText(priceRaw).replace(/[^0-9.]/g, ''));

  return {
    '@type': 'SoftwareApplication',
    name: tool.name,
    applicationCategory: tool.category_name || tool.category || 'BusinessApplication',
    offers: {
      '@type': 'Offer',
      price: Number.isFinite(numericPrice) ? numericPrice : 0,
      priceCurrency: 'USD',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: tool?.rating?.overall || tool.rating || 0,
      reviewCount: tool.review_count || tool.reviews_count || 0,
    },
  };
};

const SEOHead = ({
  pageType,
  title,
  description,
  slug,
  tool,
  faqs,
  comparisons,
  dateModified,
  author,
  category,
  noIndex = false,
}) => {
  const path = buildPath(slug);
  const canonical = `${BASE_URL}${path === '/' ? '' : path}`;
  const isoModified = toIso(dateModified);
  const isoPublished = toIso(tool?.created_at || tool?.published_at || dateModified);
  const ogImage = tool?.cover_image || tool?.logo_url || `${BASE_URL}/og-default.png`;
  const metaTitle = `${title} | Wowrolla - AI Tools Expert`;
  const faqEntities = normalizeFaq(faqs);

  const schemaGraph = useMemo(() => {
    const graph = [
      {
        '@type': 'Organization',
        '@id': `${BASE_URL}/#organization`,
        name: 'Wowrolla',
        url: BASE_URL,
        description:
          'AI tools expert platform - affiliate discovery and consulting implementation for SMBs and enterprises',
        sameAs: SAME_AS,
        logo: {
          '@type': 'ImageObject',
          url: `${BASE_URL}/logo.png`,
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: title,
        description,
      },
      createBreadcrumbs(slug),
    ];

    if (pageType === 'homepage') {
      graph.push({
        '@type': 'WebSite',
        '@id': `${BASE_URL}/#website`,
        name: 'Wowrolla',
        url: BASE_URL,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${BASE_URL}/find?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      });
    }

    if (pageType !== 'homepage') {
      graph.push({
        '@type': 'Article',
        headline: title,
        description,
        datePublished: isoPublished,
        dateModified: isoModified,
        author: {
          '@type': 'Person',
          name: author || 'Wowrolla Editorial',
        },
        publisher: {
          '@id': `${BASE_URL}/#organization`,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': canonical,
        },
        articleSection: category || pageType,
      });
    }

    if (faqEntities.length > 0) {
      graph.push({
        '@type': 'FAQPage',
        mainEntity: faqEntities,
      });
    }

    if (pageType === 'tool' && tool) {
      const softwareApp = buildSoftwareApplication(tool);
      if (softwareApp) {
        graph.push(softwareApp);
      }
    }

    if (comparisons?.rows?.length) {
      graph.push({
        '@type': 'Table',
        about: title,
      });
    }

    return {
      '@context': 'https://schema.org',
      '@graph': graph,
    };
  }, [
    author,
    canonical,
    category,
    comparisons?.rows?.length,
    description,
    faqEntities,
    isoModified,
    isoPublished,
    pageType,
    slug,
    title,
    tool,
  ]);

  return (
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />

      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:type" content={pageType === 'homepage' ? 'website' : 'article'} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      <meta property="article:modified_time" content={isoModified} />

      <script type="application/ld+json">{JSON.stringify(schemaGraph)}</script>
    </Helmet>
  );
};

export default SEOHead;

